import {
  ArrowLeftOutlined,
  PlusOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Button, Typography, Table, Space, Tooltip, Modal, Input } from "antd";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import { SpinnerLoader } from "../../../components/widgets/spinner-loader/SpinnerLoader.jsx";
import { CustomButton } from "../../../components/widgets/custom-button/CustomButton.jsx";
import { IslandLayout } from "../../../layouts/island-layout/IslandLayout.jsx";
import { useAlertStore } from "../../../store/alert-store";
import { useSessionStore } from "../../../store/session-store.js";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import SpaceWrapper from "../../../components/widgets/space-wrapper/SpaceWrapper.jsx";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import { DeleteModal } from "../../../components/deployments/delete-modal/DeleteModal.jsx";

function ManualReviewSettings() {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const [reviewKeys, setReviewKeys] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isNewKey, setIsNewKey] = useState(false);
  const [isEditKey, setIsEditKey] = useState(false);
  const [formDetails, setFormDetails] = useState({});
  const [selectedKeyRow, setSelectedKeyRow] = useState({});
  const [canAdd, setCanAdd] = useState(false);
  const handleException = useExceptionHandler();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const path = `/api/v1/unstract/${sessionDetails.orgId.replaceAll(
    '"',
    ""
  )}/manual_review`;
  useEffect(() => {
    if (isNewKey) {
      setFormDetails({
        description: "",
        is_active: true,
      });
    } else {
      setFormDetails({});
    }
  }, [isNewKey]);
  const onChangeHandler = (propertyName, value) => {
    const body = {
      [propertyName]: value,
    };
    setCanAdd(value?.trim().length > 0);
    setFormDetails({ ...formDetails, ...body });
  };
  useEffect(() => {
    if (isEditKey) {
      setFormDetails({
        description: selectedKeyRow?.description,
        is_active: selectedKeyRow?.is_active,
      });
    } else {
      setFormDetails({});
    }
  }, [isEditKey]);
  useEffect(() => {
    const option = {
      url: `${path}/api/keys/`,
      method: "GET",
    };
    setIsTableLoading(true);
    axiosPrivate(option)
      .then((result) => {
        setReviewKeys(result.data);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to get api keys"));
      })
      .finally(() => {
        setIsTableLoading(false);
      });
  }, []);
  const addKey = () => {
    const body = {
      description: formDetails.description,
    };
    const option = {
      url: `${path}/api/key/`,
      method: "POST",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    axiosPrivate(option)
      .then((result) => {
        setReviewKeys([...reviewKeys, result.data]);
        setIsNewKey(false);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to add api key"));
      });
  };
  const updateKey = () => {
    const body = {
      description: formDetails.description,
    };
    const option = {
      url: `${path}/api/key/${selectedKeyRow.id}/`,
      method: "PATCH",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };
    const updateTableData = (id, updatedRow) => {
      setReviewKeys((prevRecords) => {
        return prevRecords.map((record) => {
          if (record.id === id) {
            return updatedRow;
          }
          return record;
        });
      });
    };
    axiosPrivate(option)
      .then((result) => {
        updateTableData(selectedKeyRow?.id, result?.data);
        setAlertDetails({
          type: "success",
          content: "Key updated successfully",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to add api key"));
      })
      .finally(() => {
        setIsEditKey(false);
      });
  };

  const closeAddKey = () => {
    setIsNewKey(false);
    setIsEditKey(false);
    setCanAdd(false);
  };
  const copyText = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setIsTableLoading(false);
        setAlertDetails({
          type: "success",
          content: "Review key copied to clipboard",
        });
      })
      .catch((error) => {
        setAlertDetails({
          type: "error",
          content: "Copy failed",
        });
      });
  };
  const openAddModal = () => {
    setIsNewKey(true);
    setIsEditKey(false);
  };
  const deleteTableData = (id) => {
    setReviewKeys((prevRecords) => {
      return prevRecords.filter((record) => record.id !== id);
    });
  };
  const openEditModal = (record) => {
    setSelectedKeyRow(record);
    setIsEditKey(true);
    setIsNewKey(false);
  };
  const deleteApiKey = () => {
    const option = {
      url: `${path}/api/key/${selectedKeyRow.id}/`,
      method: "DELETE",
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
    };
    axiosPrivate(option)
      .then((result) => {
        deleteTableData(selectedKeyRow.id);
        setAlertDetails({
          type: "success",
          content: "Key deleted successfully",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err));
      })
      .finally(() => {
        setOpenDeleteModal(false);
      });
  };
  const showDeleteModal = (record) => {
    setSelectedKeyRow(record);
    setOpenDeleteModal(true);
  };
  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "API Key",
      dataIndex: "api_key",
      key: "api_key",
      render: (_, record) => (
        <Space direction="horizontal" className="action-items">
          <div>
            <Tooltip
              title="click to copy"
              className="cursorPointer"
              onClick={() => copyText(record?.api_key)}
            >
              <CopyOutlined />
            </Tooltip>
          </div>
          <div>
            <Typography.Text>{record?.api_key}</Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "pipeline_id",
      align: "center",
      render: (_, record) => (
        <>
          <Space className="actions" onClick={() => openEditModal(record)}>
            <Tooltip title="edit" className="cursorPointer">
              <EditOutlined />
            </Tooltip>
          </Space>
          <Space className="actions" onClick={() => showDeleteModal(record)}>
            <Tooltip title="delete" className="cursorPointer">
              <DeleteOutlined />
            </Tooltip>
          </Space>
        </>
      ),
    },
  ];
  return (
    <>
      <div className="plt-set-head">
        <Button size="small" type="text">
          <ArrowLeftOutlined onClick={() => navigate(-1)} />
        </Button>
        <Typography.Text className="plt-set-head-typo">
          Human In The Loop Settings
        </Typography.Text>
      </div>
      <div className="plt-set-layout">
        <IslandLayout>
          <div className="plt-set-layout-2">
            <div className="display-flex-right new-key-btn">
              <CustomButton
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openAddModal}
              >
                New Key
              </CustomButton>
            </div>
            <div className="keys-table">
              <Table
                size="small"
                dataSource={reviewKeys}
                columns={columns}
                rowKey="id"
                loading={{
                  indicator: <SpinnerLoader />,
                  spinning: isTableLoading,
                }}
                pagination={{ defaultPageSize: 5 }}
              />
            </div>
          </div>
        </IslandLayout>
      </div>
      {(isNewKey || isEditKey) && (
        <Modal
          title={isNewKey ? "Add Key" : "Update Key"}
          centered
          open={isNewKey || isEditKey}
          onOk={isNewKey ? addKey : updateKey}
          okText={isNewKey ? "Add" : "Update"}
          onCancel={closeAddKey}
          okButtonProps={{ disabled: !canAdd }}
        >
          <SpaceWrapper>
            <Typography>Description</Typography>
            <Input
              placeholder="Description"
              name="description"
              onChange={(e) => onChangeHandler("description", e.target.value)}
              value={formDetails.description || ""}
            ></Input>
          </SpaceWrapper>
        </Modal>
      )}
      {openDeleteModal && (
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          deleteRecord={deleteApiKey}
        />
      )}
    </>
  );
}

export { ManualReviewSettings };
